import { useState, useEffect, useCallback } from 'react'
import toast from 'react-hot-toast'
import Modal from '@/components/common/Modal'
import { livePreviewTransactions } from '@/livepreview/api'
import type { WorkflowBinding } from './types'

interface TxnOption {
  obj_name: string
  title: string
  main_table: string
}

interface Props {
  open: boolean
  onClose: () => void
  project: string
  binding: WorkflowBinding
  onSetBindObject: (objName: string) => void
  onSetBindTriggers: (triggers: string[]) => void
}

const ALL_TRIGGERS = ['add', 'edit'] as const

/**
 * Approval-binding editor: pick the transaction object this graphical workflow
 * approves, and which submit triggers (add / edit) route through it. Writes
 * straight into the workflow's binding state (which forces the 'advanced'
 * profile once an object is bound).
 */
export default function ApprovalBindingModal({
  open, onClose, project, binding, onSetBindObject, onSetBindTriggers,
}: Props) {
  const [txns, setTxns] = useState<TxnOption[]>([])
  const [loading, setLoading] = useState(false)

  // Local draft so the user can stage the object + triggers, then Apply.
  const [obj, setObj] = useState(binding.bindObject || '')
  const [triggers, setTriggers] = useState<string[]>(binding.bindTriggers?.length ? binding.bindTriggers : ['add'])

  useEffect(() => {
    if (!open) return
    setObj(binding.bindObject || '')
    setTriggers(binding.bindTriggers?.length ? binding.bindTriggers : ['add'])
  }, [open, binding.bindObject, binding.bindTriggers])

  useEffect(() => {
    if (!open || !project) return
    let cancelled = false
    setLoading(true)
    livePreviewTransactions(project)
      .then(res => { if (!cancelled) setTxns(res.transactions || []) })
      .catch(() => { if (!cancelled) toast.error('Failed to load transaction objects') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [open, project])

  const toggleTrigger = useCallback((t: string) => {
    setTriggers(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t])
  }, [])

  const handleApply = useCallback(() => {
    if (obj && triggers.length === 0) {
      toast.error('Select at least one trigger (Add or Edit)')
      return
    }
    onSetBindObject(obj)
    if (obj) onSetBindTriggers(triggers)
    toast.success(obj ? 'Approval binding updated' : 'Approval binding cleared')
    onClose()
  }, [obj, triggers, onSetBindObject, onSetBindTriggers, onClose])

  return (
    <Modal open={open} onClose={onClose} title="Approval binding" size="wide">
      <div style={{ fontSize: '0.8625rem', color: 'var(--color-text-secondary)', marginBottom: 12 }}>
        Bind this workflow to a transaction so its add/edit submits route through
        the approval steps below. Leave the object blank to make this a plain
        (non-approval) workflow.
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={labelStyle}>Bound transaction object</label>
        <select
          value={obj}
          onChange={e => setObj(e.target.value)}
          disabled={loading}
          style={inputStyle}
        >
          <option value="">{loading ? 'Loading…' : '— None (plain workflow) —'}</option>
          {txns.map(t => (
            <option key={t.obj_name} value={t.obj_name}>
              {t.title ? `${t.title} (${t.obj_name})` : t.obj_name}
            </option>
          ))}
        </select>
      </div>

      <div style={{ marginBottom: 12, opacity: obj ? 1 : 0.5, pointerEvents: obj ? 'auto' : 'none' }}>
        <label style={labelStyle}>Triggers</label>
        <div style={{ display: 'flex', gap: 16, marginTop: 4 }}>
          {ALL_TRIGGERS.map(t => (
            <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8825rem', color: 'var(--color-text)', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={triggers.includes(t)}
                onChange={() => toggleTrigger(t)}
              />
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </label>
          ))}
        </div>
        <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', opacity: 0.7, marginTop: 4, display: 'block' }}>
          Which submit actions on the bound object require this approval.
        </span>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
        <button onClick={onClose} className="btn btn-ghost btn-sm">Cancel</button>
        <button onClick={handleApply} className="btn btn-primary btn-sm">Apply</button>
      </div>
    </Modal>
  )
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary)',
  marginBottom: 2,
  fontWeight: 500,
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 8px',
  fontSize: '0.8825rem',
  background: 'var(--color-bg-secondary)',
  border: '1px solid var(--color-border)',
  borderRadius: 4,
  color: 'var(--color-text)',
  outline: 'none',
  boxSizing: 'border-box',
}
